/**
 * Smart Alert Deduplication Service
 * Prevents duplicate alerts unless previous alerts are resolved
 */

class AlertDeduplicationService {
  /**
   * Generate unique alert signature
   * Alerts with same signature are considered duplicates
   */
  generateAlertSignature(entityId, alertType, severity) {
    return `${entityId}|${alertType}|${severity}`;
  }

  /**
   * Check if alert already exists (unresolved)
   * @param {Array} existingAlerts - Current unresolved alerts
   * @param {Object} newAlert - New alert to check
   * @returns {boolean} True if duplicate exists
   */
  isDuplicate(existingAlerts, newAlert) {
    const newSignature = this.generateAlertSignature(
      newAlert.EntityIdLookupId || newAlert.entityId,
      newAlert.AlertType || this.determineAlertType(newAlert.Message),
      newAlert.Severity,
    );

    return existingAlerts.some((alert) => {
      const existingSignature = this.generateAlertSignature(
        alert.EntityIdLookupId || alert.EntityId,
        alert.AlertType || this.determineAlertType(alert.Message),
        alert.Severity,
      );

      return existingSignature === newSignature && !alert.IsResolved;
    });
  }

  /**
   * Determine alert type from message
   * Used to categorize alerts
   */
  determineAlertType(message) {
    if (!message) return "General";

    const msg = message.toLowerCase();

    if (
      msg.includes("overdue") ||
      msg.includes("delayed") ||
      msg.includes("deadline")
    ) {
      return "Deadline";
    }
    if (msg.includes("blocked") || msg.includes("blocking")) {
      return "Dependency";
    }
    if (
      msg.includes("target") ||
      msg.includes("below") ||
      msg.includes("variance")
    ) {
      return "Performance";
    }
    if (msg.includes("risk") || msg.includes("at risk")) {
      return "Risk";
    }

    return "General";
  }

  /**
   * Filter out duplicate alerts before creation
   * @param {Array} newAlerts - Alerts to be created
   * @param {Array} existingAlerts - Current unresolved alerts
   * @returns {Array} Unique alerts only
   */
  filterDuplicates(newAlerts, existingAlerts) {
    return newAlerts.filter((newAlert) => {
      const isDupe = this.isDuplicate(existingAlerts, newAlert);

      if (isDupe) {
        console.log(
          `[Alert Dedup] Skipping duplicate alert: ${newAlert.Message}`,
        );
      }

      return !isDupe;
    });
  }

  /**
   * Batch create alerts with deduplication
   * @param {Array} alertsToCreate - New alerts
   * @param {Array} existingAlerts - Current alerts
   * @param {Function} createAlertFn - Function to create alert
   * @returns {Object} Creation results
   */
  async batchCreateWithDeduplication(
    alertsToCreate,
    existingAlerts,
    createAlertFn,
  ) {
    // Filter out duplicates
    const uniqueAlerts = this.filterDuplicates(alertsToCreate, existingAlerts);

    console.log(
      `[Alert Dedup] Total: ${alertsToCreate.length}, Unique: ${uniqueAlerts.length}, Skipped: ${alertsToCreate.length - uniqueAlerts.length}`,
    );

    // Create unique alerts
    const created = [];
    const errors = [];

    for (const alert of uniqueAlerts) {
      try {
        // Add alert type for better categorization
        const alertWithType = {
          ...alert,
          AlertType: alert.AlertType || this.determineAlertType(alert.Message),
        };

        await createAlertFn(alertWithType);
        created.push(alertWithType);
      } catch (error) {
        console.error("[Alert Dedup] Error creating alert:", error);
        errors.push({ alert, error: error.message });
      }
    }

    return {
      total: alertsToCreate.length,
      created: created.length,
      duplicates: alertsToCreate.length - uniqueAlerts.length,
      errors: errors.length,
      createdAlerts: created,
      errorDetails: errors,
    };
  }

  /**
   * Get alert summary by type
   * @param {Array} alerts - All alerts
   * @returns {Object} Summary by type
   */
  getAlertSummary(alerts) {
    const summary = {
      total: alerts.length,
      byType: {},
      bySeverity: {
        Critical: 0,
        Warning: 0,
        Info: 0,
      },
      resolved: 0,
      unresolved: 0,
    };

    alerts.forEach((alert) => {
      // Count by type
      const type = alert.AlertType || this.determineAlertType(alert.Message);
      summary.byType[type] = (summary.byType[type] || 0) + 1;

      // Count by severity
      const severity = alert.Severity || "Info";
      summary.bySeverity[severity] = (summary.bySeverity[severity] || 0) + 1;

      // Count resolved vs unresolved
      if (alert.IsResolved) {
        summary.resolved++;
      } else {
        summary.unresolved++;
      }
    });

    return summary;
  }

  /**
   * Auto-resolve alerts when condition is fixed
   * @param {Array} alerts - Current alerts
   * @param {Array} entities - Current entities with status
   * @param {Function} resolveAlertFn - Function to resolve alert
   */
  async autoResolveFixedAlerts(alerts, entities, resolveAlertFn) {
    const resolved = [];
    const unresolvedAlerts = alerts.filter((a) => !a.IsResolved);

    for (const alert of unresolvedAlerts) {
      const entity = entities.find(
        (e) => e.Id === (alert.EntityIdLookupId || alert.EntityId),
      );

      if (!entity) continue;

      let shouldResolve = false;
      const alertType =
        alert.AlertType || this.determineAlertType(alert.Message);

      // Auto-resolve based on alert type
      switch (alertType) {
        case "Deadline":
          // Resolve if project is now completed
          if (entity.IsCompleted) {
            shouldResolve = true;
          }
          break;

        case "Performance":
          // Resolve if performance is now meeting target
          if (entity.ytdMetrics && entity.ytdMetrics.status === "Met") {
            shouldResolve = true;
          }
          break;

        case "Risk":
          // Resolve if no longer at risk
          if (entity.ytdMetrics && entity.ytdMetrics.status === "Met") {
            shouldResolve = true;
          }
          if (
            entity.deadlineStatus &&
            entity.deadlineStatus.status === "On Track"
          ) {
            shouldResolve = true;
          }
          break;
      }

      if (shouldResolve) {
        try {
          await resolveAlertFn(alert.Id);
          resolved.push(alert);
          console.log(`[Alert Dedup] Auto-resolved alert: ${alert.Message}`);
        } catch (error) {
          console.error("[Alert Dedup] Error auto-resolving alert:", error);
        }
      }
    }

    return {
      autoResolved: resolved.length,
      resolvedAlerts: resolved,
    };
  }
}

export default new AlertDeduplicationService();
